import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { translate } from '@jsverse/transloco';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { TimeDurationPipe } from 'src/app/pipes/timeDuration.pipe';
import { ActivityService } from 'src/app/services/api/activity.service';
import { ActivityProgressService } from 'src/app/services/util/activity-progress.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';

@Component({
  selector: 'app-environment-activity-details',
  templateUrl: './environment-activity-details.html',
  styleUrl: './environment-activity-details.scss',
  standalone: false,
  providers: [TimeDurationPipe],
})
export class EnvironmentActivityDetails implements OnInit {
  private destroyRef = inject(DestroyRef);
  @Input() activityDetail: any;
  @Input() instance: any;
  progressTimer: any;
  countData: any = { total: 0, completed: 0, pending: 0, skipped: 0 };
  currentTime = Date.now();
  estimatedtime: any;
  instanceId: any;
  activityId: any;
  isComponentAvailable: boolean;
  allComponents: any = [];
  FilterColumns = [
    { key: 'all', label: translate('ACTIVITY_DETAILS.ALL'), count: 0 },
    { key: 'inprogress', label: translate('ACTIVITY_DETAILS.INPROGRESS'), count: 0 },
    { key: 'completed', label: translate('ACTIVITY_DETAILS.COMPLETED'), count: 0 },
    { key: 'failed', label: translate('ACTIVITY_DETAILS.FAILED'), count: 0 },
    { key: 'threshold', label: translate('ACTIVITY_DETAILS.THRESHOLD'), count: 0 },
  ];
  selectedStatus: any = "all";
  filteredComponents: any = [];
  dataLoading : boolean = true
  displayColumns = [
    {
      key: 'name',
      name: translate('ACTIVITY_DETAILS.COMPONENT_NAME'),
      type: 'components',
      sortable: true,
      visible: true
    },
    {
      key: 'estimatedTime',
      name: translate('ACTIVITY_DETAILS.ESTIMATED_TIME'),
      type: 'estimatedTime',
      sortable: true,
      visible: true
    },
  ]
  noDataMessage: string;
  componentExecStage: any;
  instanceStatus: string;

  constructor(
    public activityProgressService: ActivityProgressService,
    private activityService: ActivityService,
    private sidenavService: SidenavService,
    private timeDuration: TimeDurationPipe,
    private toastrService: CustomToastrService,
  ) {}

  ngOnInit(): void {
    this.instanceId = this.instance.id;
    this.activityId = this.activityDetail?.id;
    this.fetchActivityDetail();
    this.instanceStatus = this.instance?.lastOperation?.executionPhase === DeployStatus.ABORTING ? this.instance?.lastOperation?.executionPhase + ' ' + this.instance?.lastLifecycleOperation?.type : this.instance?.status === DeployStatus.INPROGRESS ? translate(this.activityProgressService.getEnvironmentStatus(this.instance?.lastLifecycleOperation?.type,this.instance?.status)) : this.instance?.status;
  }

  // Processes activity data and prepares component status and counts.
  processActivityDetails() {
    // clear old interval
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
    }

    const stages: any[] = this.activityDetail?.stages ?? [];
    const execStage = stages.find((s) => s.type === 'component_execution');
    this.componentExecStage = execStage;
    this.allComponents = execStage?.components.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()) ?? [];
    this.isComponentAvailable = this.allComponents.length > 0;
    if (execStage?.count?.total) {
      this.countData.total = Number(execStage.count.total);
      this.countData.completed = Number(execStage.count.completed ?? 0);
      this.countData.skipped = Number(execStage.count.skipped   ?? 0);
      this.countData.pending = Number(this.countData.total - (this.countData.completed + this.countData.skipped));
    }
    this.updateComponentEstimates();
    // new interval with fresh data
    this.progressTimer = setInterval(() => {
      this.updateComponentEstimates();
    }, 1000);
  }
  
  /**
   * Updates time/progress information every second.
   * Uses the latest API data from componentExecStage.
   */
  updateComponentEstimates(): void {
    this.currentTime = Date.now();
    this.estimatedtime = this.activityProgressService.getTimeRemaining(this.normalizeUtc(this.activityDetail?.startTime),this.activityDetail?.expectedDurationMs,this.currentTime);
    if (this.isComponentAvailable) {
      const sortedComponents = this.componentExecStage?.components.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
      this.allComponents = (sortedComponents ?? []).map(comp => {
        const thresholdReached = Boolean(comp?.start && comp?.expectedDurationMs && this.activityProgressService.isThresholdReached( this.normalizeUtc(comp.start), comp.expectedDurationMs,this.currentTime) && comp.health?.warning === "true" && !comp?.finishTime);
        let estimatedTime: string;
        let timeLeft = this.timeDuration.transform(comp?.start, comp?.end, "")
        if (comp.status === 'success') { 
          estimatedTime = 'Completed';
        } else if (comp.status === 'failed') {
          estimatedTime = 'Failed';
        } else if (comp.status === 'inprogress' && thresholdReached) {
          estimatedTime = 'Threshold Reached';
        } else {
          estimatedTime = this.activityProgressService.formatTimeRemaining(this.normalizeUtc(comp?.start),comp?.expectedDurationMs,this.currentTime);
        }
        return {...comp, thresholdReached, estimatedTime, timeLeft};
      });
      // apply status priority
      const statusPriority = (comp: typeof this.allComponents[number]) => {
        if (comp.status === 'inprogress' && !comp.thresholdReached) return 1;
        if (comp.status === 'inprogress' && comp.thresholdReached) return 2;
        if (comp.status === 'failed') return 3;
        if (comp.status === 'success') return 4;
        return 5;
      };
      this.allComponents.sort((a, b) => statusPriority(a) - statusPriority(b));
      const statusCount = {
        "all" : this.allComponents.length,
        "inprogress": this.allComponents.filter( (c) => c.status === 'inprogress' && !c.thresholdReached).length,
        "completed": this.allComponents.filter( (c) => c.status === 'success').length,
        "failed": this.allComponents.filter((c) => c.status === 'failed').length,
        "threshold": this.allComponents.filter((c) => c.status === 'inprogress' && c.thresholdReached).length
      }
      this.FilterColumns = this.FilterColumns.map(button => ({...button, count: statusCount[button.key]}));
      this.filterComponents();
    } else {
      this.dataLoading= false;
    }
  }

  // Filters components based on the selected status.
  filterComponents(): void {
    const statusFilters = {
      all: (c: any) => true,
      inprogress: (c: any) => c.status === 'inprogress' && !c.thresholdReached,
      completed: (c: any) => c.status === 'success',
      failed: (c: any) => c.status === 'failed',
      threshold: (c: any) => c.status === 'inprogress' && c.thresholdReached
    };
    const filter = statusFilters[this.selectedStatus as keyof typeof statusFilters];
    this.filteredComponents = this.allComponents.filter(filter);
    this.dataLoading = false;
    if (this.filteredComponents.length === 0) {
      this.setEmptyMessage();
    }
  }

  normalizeUtc(dateStr) {
    return dateStr?.endsWith('Z') ? dateStr : dateStr + 'Z';
  }

  // Fetches full activity detail by ID.
  fetchActivityDetail(): void {
    this.dataLoading = true;
    this.activityService
      .getInstanceActivityLogsById(this.instanceId, this.activityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          response.instanceId = this.instanceId;
          this.activityDetail = response;
          // update sidenav data to get lastest record
          this.sidenavService.updateSidenavData(this.activityDetail);
          this.processActivityDetails();
        },
        error: (error) => {
          this.dataLoading = false;
          setTimeout(() => {
            const title = translate('ERRORS.SERVICE_AVAILABILITY');
            const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
            this.toastrService.errMessage(message, title);
          }, 500);
        },
      });
  }

  // Selects a status filter and updates the component list.
  selectStatus(status: string): void {
    this.selectedStatus = status;
    this.filterComponents();
  }

  closepopup(): void {
    const updatedActivityDetail = {
      ...this.activityDetail,
      instanceId: this.instanceId
    };
    this.sidenavService.closeRightSidenav(updatedActivityDetail);
  }

  // Refreshes the activity details.
  refreshClick(): void {
    this.fetchActivityDetail();
  }

  // Sets the message displayed when no components are available.
  setEmptyMessage(): void {
    const selectedFilter = this.FilterColumns.find(button => button.key === this.selectedStatus);
    this.noDataMessage = !selectedFilter || selectedFilter.key === 'all' ? translate('ACTIVITY_DETAILS.EMPTY_COMPONENTS') : translate('ACTIVITY_DETAILS.EMPTY_COMPONENTS_STATUS',{ status: this.selectedStatus});
  }

  ngOnDestroy(): void {
    if (this.progressTimer) clearInterval(this.progressTimer);
  }
  
  get progressPercent(): number {
    const completed = this.countData?.completed ?? 0;
    const total = this.countData?.total ?? 0;
    if (total === 0) { return 0; }
    const minPercent = (12 / 615) * 100; // 12px minimum fill
    return Math.min(Math.max((completed / total) * 100, minPercent), 100);
  }
}
